import React from 'react';

import { formatDate } from '../../utils/format-date';
import { formatCurrency } from '../../utils/format-currency';
import { safeArray } from '../../utils/safe';
import type { ReceiptData } from '../../utils/receipt';
import { getBusinessConfig } from '../../hooks/useBusinessConfig';
import { getPrinterConfig, getTicketMetrics } from '../../hooks/usePrinterConfig';

interface ReceiptTicketProps {
  data: ReceiptData;
}

export const ReceiptTicket = React.forwardRef<HTMLDivElement, ReceiptTicketProps>(
  ({ data }, ref) => {
    const biz = getBusinessConfig();
    const printer = getPrinterConfig();
    const metrics = getTicketMetrics(printer.paperWidth);
    const D = '─'.repeat(metrics.chars); // divisor según el ancho del papel

    const safeCurrency = (amount?: number | null) => {
      if (amount === undefined || amount === null || !Number.isFinite(amount)) return formatCurrency(0);
      return formatCurrency(amount);
    };

    const items = safeArray(data.items);
    const payments = safeArray(data.payments);
    const totalPayments = payments.reduce((s, p) => s + p.amount, 0);
    const totalReceived = payments.reduce((s, p) => s + (p.received ?? p.amount), 0) ?? data.received;
    const change = data.change ?? (totalReceived != null && totalReceived > data.total ? totalReceived - data.total : undefined);

    return (
      <div
        ref={ref}
        data-testid="receipt-ticket"
        className="print:block"
        style={{
          width: `${metrics.widthMm}mm`,
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: `${metrics.fontSize}px`,
          lineHeight: '1.5',
          color: '#000',
          padding: metrics.widthMm <= 58 ? '6px 6px' : '8px 10px',
          background: 'white',
        }}
      >
        {/* ── LOGO ── */}
        {biz.logoUrl && (
          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
            <img
              src={biz.logoUrl}
              alt="Logo"
              style={{ maxWidth: '120px', maxHeight: '60px', objectFit: 'contain', display: 'inline-block' }}
            />
          </div>
        )}

        {/* ── BUSINESS HEADER ── */}
        <div style={{ textAlign: 'center', marginBottom: '4px' }}>
          <div style={{ fontSize: '15px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {biz.name || 'Mi Restaurante'}
          </div>
          {biz.slogan && (
            <div style={{ fontSize: '10px', fontStyle: 'italic', marginTop: '2px' }}>{biz.slogan}</div>
          )}
          {biz.address && (
            <div style={{ fontSize: '10px', marginTop: '2px' }}>{biz.address}</div>
          )}
          {biz.phone && (
            <div style={{ fontSize: '10px' }}>Tel: {biz.phone}</div>
          )}
          {biz.nit && (
            <div style={{ fontSize: '10px' }}>NIT: {biz.nit}</div>
          )}
        </div>

        <div style={{ borderBottom: '1px dashed #000', margin: '8px 0' }} />

        {/* ── ORDER META ── */}
        <div style={{ fontSize: '11px', marginBottom: '6px' }}>
          <Row label="Fecha" value={formatDate(data.paidAt, 'dd/MM/yy HH:mm')} />
          {data.orderNumber && <Row label="Orden #" value={String(data.orderNumber)} bold />}
          {data.tableLabel && <Row label="Mesa/Tipo" value={data.tableLabel} />}
          {data.customerName && <Row label="Cliente" value={data.customerName} />}
        </div>

        <div style={{ borderBottom: '1px dashed #000', margin: '8px 0' }} />

        {/* ── ITEMS ── */}
        <div style={{ fontSize: '11px' }}>
          <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: '3px', marginBottom: '4px' }}>
            <span style={{ width: '8%' }}>Cnt</span>
            <span style={{ width: '62%' }}>Descripción</span>
            <span style={{ width: '30%', textAlign: 'right' }}>Total</span>
          </div>

          {items.map((item, index) => (
            <div key={`${item.name}-${index}`} style={{ marginBottom: '5px' }}>
              <div style={{ display: 'flex' }}>
                <span style={{ width: '8%' }}>{item.quantity}</span>
                <span style={{ width: '62%', paddingRight: '4px', wordBreak: 'break-word' }}>
                  {item.name}
                  {item.unitPrice !== item.total / item.quantity && item.quantity > 1 && (
                    <span style={{ fontSize: '9px', color: '#555', display: 'block' }}>
                      c/u {safeCurrency(item.unitPrice)}
                    </span>
                  )}
                </span>
                <span style={{ width: '30%', textAlign: 'right' }}>{safeCurrency(item.total)}</span>
              </div>
              {item.notes && (
                <div style={{ fontSize: '10px', fontStyle: 'italic', margin: '1px 0 0 8%', color: '#333' }}>
                  ✎ {item.notes}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ borderBottom: '1px dashed #000', margin: '8px 0' }} />

        {/* ── TOTALS ── */}
        <div style={{ fontSize: '11px', marginBottom: '8px' }}>
          <Row label="Subtotal" value={safeCurrency(data.subtotal)} />
          {!!data.discountAmount && data.discountAmount > 0 && (
            <Row label="Descuento" value={`- ${safeCurrency(data.discountAmount)}`} />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 'bold', borderTop: '1px solid #000', marginTop: '4px', paddingTop: '4px' }}>
            <span>TOTAL</span>
            <span>{safeCurrency(data.total)}</span>
          </div>
        </div>

        {/* ── PAYMENTS ── */}
        {(payments.length > 0) ? (
          <div style={{ fontSize: '11px', marginBottom: '8px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>Forma de pago:</div>
            {payments.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{p.method}</span>
                <span>{safeCurrency(p.amount)}</span>
              </div>
            ))}
            {typeof totalReceived === 'number' && totalReceived > data.total && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                  <span>Recibido</span>
                  <span>{safeCurrency(totalReceived)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <span>Cambio</span>
                  <span>{safeCurrency(totalReceived - data.total)}</span>
                </div>
              </>
            )}
          </div>
        ) : (
          (typeof data.received === 'number' || typeof data.change === 'number') && (
            <div style={{ fontSize: '11px', marginBottom: '8px' }}>
              {typeof data.received === 'number' && <Row label="Recibido" value={safeCurrency(data.received)} />}
              {typeof change === 'number' && change > 0 && <Row label="Cambio" value={safeCurrency(change)} />}
            </div>
          )
        )}

        <div style={{ borderBottom: '1px dashed #000', margin: '8px 0' }} />

        {/* ── FOOTER ── */}
        <div style={{ textAlign: 'center', fontSize: '10px', paddingBottom: '6px' }}>
          {(biz.receiptFooter || '¡Gracias por su visita! Vuelva pronto.').split('\n').map((line, i) => (
            <p key={i} style={{ margin: '3px 0' }}>{line}</p>
          ))}
        </div>
      </div>
    );
  },
);

ReceiptTicket.displayName = 'ReceiptTicket';

// ── Helper ───────────────────────────────────────────────────────────────────
function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: bold ? 'bold' : 'normal' }}>
      <span>{label}:</span>
      <span style={{ maxWidth: '65%', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

export default ReceiptTicket;
